import React, { useRef, useState, useMemo, useCallback } from 'react';
import { 
  Box, Card, CardContent, Typography, Button, Avatar, Divider, 
  TextField, LinearProgress
} from '@mui/material';
import { CheckCircle, Image } from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import { useChatContext } from '../../agent/contexts/ChatContext';
import { storageService } from '../../../services/storageService';
import BiometricStepsHeader from './BiometricStepsHeader';

interface BiometricCropViewProps {
  cropData?: {
    animalName?: string;
    cattleTag?: string;
  };
}

// Util: converter File para base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const BiometricCropView: React.FC<BiometricCropViewProps> = ({ cropData }) => {
  const { sendManagerMessageWithContext } = useChatContext();
  const { sessions, activeSessionId } = useChatContext();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [sending, setSending] = useState<boolean>(false);
  const [cropping, setCropping] = useState<boolean>(false);
  const [cropResult, setCropResult] = useState<string>('');

  // Crop state (simple rectangle)
  const [crop, setCrop] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const title = useMemo(() => 'Rastreabilidade: você está fazendo do jeito certo!', []);
  const hint = useMemo(() => 'Recorte apenas o focinho do animal antes de enviar. Isso aumenta a qualidade da biometria.', []);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    setError('');
    setCropResult('');
    if (file) {
      try {
        const b64 = await fileToBase64(file);
        setImageSrc(b64);
      } catch {
        setError('Falha ao carregar a imagem. Tente novamente.');
        setImageSrc('');
      }
    } else {
      setImageSrc('');
    }
  };

  // Mouse handlers para definir o retângulo de recorte
  const [dragging, setDragging] = useState<boolean>(false);
  const [startPos, setStartPos] = useState<{ x: number; y: number } | null>(null);

  const getRelativePos = useCallback((clientX: number, clientY: number) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!imageSrc) return;
    setDragging(true);
    const pos = getRelativePos(e.clientX, e.clientY);
    setStartPos(pos);
    setCrop({ x: pos.x, y: pos.y, w: 1, h: 1 });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging || !startPos) return;
    const pos = getRelativePos(e.clientX, e.clientY);
    const w = pos.x - startPos.x;
    const h = pos.y - startPos.y;
    setCrop({ x: Math.min(startPos.x, pos.x), y: Math.min(startPos.y, pos.y), w: Math.abs(w), h: Math.abs(h) });
  };

  const handleMouseUp = () => {
    setDragging(false);
    setStartPos(null);
  };

  const performCrop = () => {
    if (!imgRef.current || !crop) {
      setError('Selecione uma área de recorte na imagem.');
      return;
    }
    setCropping(true);
    try {
      const img = imgRef.current;
      const container = containerRef.current!;
      // Calcular escala entre imagem renderizada e tamanho natural
      const renderWidth = img.width;
      const renderHeight = img.height;
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      const scaleX = naturalWidth / renderWidth;
      const scaleY = naturalHeight / renderHeight;

      // Criar canvas do recorte
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.floor(crop.w * scaleX));
      canvas.height = Math.max(1, Math.floor(crop.h * scaleY));
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas não suportado');
      ctx.drawImage(
        img,
        Math.floor(crop.x * scaleX),
        Math.floor(crop.y * scaleY),
        Math.floor(crop.w * scaleX),
        Math.floor(crop.h * scaleY),
        0,
        0,
        canvas.width,
        canvas.height
      );
      const result = canvas.toDataURL('image/jpeg', 0.95);
      // Substitui a imagem exibida pela versão recortada
      setImageSrc(result);
      setCropResult(result);
      setCrop(null);
      setError('');
    } catch (err) {
      setError('Falha ao recortar a imagem.');
    } finally {
      setCropping(false);
    }
  };

  const handleEnviar = async () => {
    try {
      setSending(true);
      setError('');
      const payloadBase64 = cropResult || imageSrc;
      if (!payloadBase64) {
        setError('Selecione e recorte a imagem antes de enviar.');
        setSending(false);
        return;
      }
      // Converter dataURL para Blob
      const dataUrlToBlob = (dataUrl: string): Blob => {
        const [meta, base64Data] = dataUrl.split(',');
        const mimeMatch = meta.match(/data:(.*?);base64/);
        const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
        const byteChars = atob(base64Data);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mime });
      };

      const blob = dataUrlToBlob(payloadBase64);

      // Recuperar farmId da sessão ativa
      const session = sessions.find((s) => s.session_id === activeSessionId);
      const farmId = session?.organization_id || '';
      const cattleTag = cropData?.cattleTag || '';
      if (!farmId || !cattleTag) {
        setError('Contexto incompleto: farmId ou cattleTag ausente.');
        setSending(false);
        return;
      }

      // Upload para bucket biometria e obter URL
      const uploadRes = await storageService.uploadBiometricImage({
        farmId,
        animalTagOrId: cattleTag,
        file: blob,
        contentType: blob.type || 'image/jpeg',
      });

      await sendManagerMessageWithContext(
        'Cadastrar biometria do animal',
        'animal',
        {
          cadastro_inserir_biometria: 'true',
          muzzle_biometrics_base64: uploadRes.objectKey,
          cattle_tag: cattleTag,
        }
      );
    } catch (err) {
      setError('Erro ao enviar a biometria.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <BiometricStepsHeader active={1} />
      {/* Mensagem incentivadora */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: DESIGN_TOKENS.colors.brand.primary, color: DESIGN_TOKENS.colors.background.paper }}>
              <CheckCircle />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
              <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                {hint}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Layout 2 colunas */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Esquerda: imagem exemplo e instrução */}
        <Box sx={{ flex: '1 1 320px', minWidth: 300 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Exemplo de Seleção do Focinho
              </Typography>
              <Box
                sx={{
                  border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}
              >
                <Box
                  component="img"
                  src="/biovaca.png"
                  alt="Exemplo: selecione a área do focinho como mostrado"
                  sx={{
                    display: 'block',
                    width: '100%',
                    maxHeight: 320,
                    objectFit: 'cover',
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ mt: 1.5, color: DESIGN_TOKENS.colors.text.secondary }}>
                Selecione a área do focinho do animal assim como exemplificado na foto.
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Direita: upload + visualizador com recorte */}
        <Box sx={{ flex: '2 1 480px', minWidth: 380 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar variant="rounded" sx={{ bgcolor: DESIGN_TOKENS.colors.secondary[100], color: DESIGN_TOKENS.colors.secondary[700] }}>
                  <Image />
                </Avatar>
                <TextField 
                  type="file" 
                  inputProps={{ accept: 'image/*' }}
                  onChange={handleFileChange}
                  helperText={imageSrc ? 'Imagem carregada. Selecione a área do focinho para recortar.' : 'Selecione a imagem do focinho para cadastrar biometria.'}
                  sx={{ flex: 1 }}
                />
              </Box>

              {imageSrc && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: DESIGN_TOKENS.colors.text.secondary }}>
                    Selecione a área do recorte arrastando sobre a imagem
                  </Typography>
                  <Box 
                    ref={containerRef}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    sx={{
                      position: 'relative',
                      display: 'inline-block', // Container ajusta ao tamanho da imagem
                      border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                      borderRadius: 2,
                      overflow: 'hidden',
                      cursor: 'crosshair',
                      maxHeight: 320,
                    }}
                  >
                    <img 
                      ref={imgRef}
                      src={imageSrc}
                      alt="Biometria"
                      style={{ 
                        display: 'block', 
                        maxWidth: '100%', 
                        height: 'auto', 
                        maxHeight: '320px', // Mantém mesma altura do exemplo à esquerda
                        userSelect: 'none' 
                      }}
                      draggable={false}
                    />
                    {crop && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: crop.x,
                          top: crop.y,
                          width: crop.w,
                          height: crop.h,
                          border: `2px solid ${DESIGN_TOKENS.colors.primary[500]}`,
                          backgroundColor: 'rgba(107, 196, 60, 0.15)',
                          boxShadow: '0 0 0 9999px rgba(0,0,0,0.15)', // Destaque a área recortada
                        }}
                      />
                    )}
                  </Box>

                  {/* A imagem exibida acima já é substituída pela recortada */}

                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button variant="outlined" onClick={performCrop} disabled={!imageSrc || !crop || cropping}>
                      Recortar
                    </Button>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={handleEnviar} 
                      disabled={sending || !cropResult}
                    >
                      Enviar biometria
                    </Button>
                  </Box>

                  {(cropping || sending) && (
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress />
                    </Box>
                  )}
                </Box>
              )}

              {error && (
                <Typography variant="body2" sx={{ mt: 2, color: DESIGN_TOKENS.colors.status.error[600] }}>
                  {error}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default BiometricCropView;